import React, { useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { USAContext } from "./USAContext";
import { saveAs } from "file-saver";
import { PDFDocument, rgb } from "pdf-lib";
import { fetchUpsMergedLabels } from "./MergeUPSLabels";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Alert,
  AlertDescription,
  Progress,
  Badge
} from '../components/ui';
import {
  CreditCard,
  Download,
  Upload,
  FileText,
  Loader2,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Package,
  Truck,
  TestTube2
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL;

const PurchaseLabels = () => {
  const { shipmentInfo, clientsData, boxes, asinData, selectedPlan, trackingCodeMap, fetchPlanDetails, getTrackingCodeForBox } =
    useContext(USAContext);

  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clientName, setClientName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [requiredCredits, setRequiredCredits] = useState(0);
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [labels, setLabels] = useState([]);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [stallionPdfBytes, setStallionPdfBytes] = useState(null);
  const [upsPdfUrl, setUpsPdfUrl] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [adjustedAddresses, setAdjustedAddresses] = useState({});
  const [refreshUpsLabels, setRefreshUpsLabels] = useState(false);
  const [upsLabelsLoading, setUpsLabelsLoading] = useState(false);
  const [upsFile, setUpsFile] = useState(null);
  const [stallionFile, setStallionFile] = useState(null);
  const [upsFileName, setUpsFileName] = useState("");
  const [stallionFileName, setStallionFileName] = useState("");
  const [importInfo, setImportInfo] = useState({});
  const [dragOverUps, setDragOverUps] = useState(false);
  const [dragOverStallion, setDragOverStallion] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch UPS labels on component mount or when selectedPlan changes
  useEffect(() => {
    if (selectedPlan && selectedPlan.placement_options) {
      console.log("Getting earliest placement option...");
      const earliestConfirmedOption = selectedPlan.placement_options.reduce(
        (earliest, option) => {
          if (
            option.confirmed_at &&
            (!earliest || new Date(option.confirmed_at) < new Date(earliest.confirmed_at))
          ) {
            return option;
          }
          return earliest;
        },
        null
      );

      if (earliestConfirmedOption) {
        console.log("Found earliest placement option.");
      } else {
        console.error("No confirmed placement option found.");
      }
    }
  }, [selectedPlan, refreshUpsLabels]);

  // Fetch API key and credits if not in test mode
  useEffect(() => {
    if (!testMode && shipmentInfo && clientsData) {
      const client = clientsData.find(
        (client) => client.id === shipmentInfo.team_id
      );
      if (client) {
        fetchApiKey(client.name);
      } else {
        console.log("No client found for team_id:", shipmentInfo.team_id);
      }
    } else if (!testMode) {
      console.log("shipmentInfo or clientsData not available yet");
    }
  }, [shipmentInfo, clientsData, testMode]);

  // Calculate required credits when boxes change
  useEffect(() => {
    if (boxes) {
      calculateRequiredCredits(boxes);
      console.log("Selected Plan:", selectedPlan);
    }
  }, [boxes, selectedPlan]);

  // Fetch adjusted addresses from the database
  useEffect(() => {
    if (selectedPlan) {
      fetchAdjustedAddresses();
      // Fetch detailed plan info to get tracking codes
      fetchPlanDetails(selectedPlan.id);
    }
  }, [selectedPlan]);

  // Fetch import info when boxes change
  useEffect(() => {
    if (boxes && boxes.length > 0) {
      const asins = [...new Set(boxes.flatMap(box => 
        box.items.map(item => item.ASIN)
      ))].filter(asin => asin); // Filter out empty/null ASINs
      
      // Only fetch import info if we have ASINs
      if (asins.length > 0) {
        console.log('Fetching import info for ASINs:', asins); // Debug log
        fetchImportInfo(asins);
      } else {
        console.log('No ASINs found, skipping import info fetch');
        setImportInfo({});
      }
    }
  }, [boxes, selectedPlan]); // Add selectedPlan as dependency

  // Fetch adjusted addresses from the database
  const fetchAdjustedAddresses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/warehouse-addresses`);
      const data = await response.json();
      const adjustedAddressesMap = {};
      data.forEach(address => {
        adjustedAddressesMap[address.warehouse_id] = address;
      });
      setAdjustedAddresses(adjustedAddressesMap);
    } catch (error) {
      console.error('Error fetching adjusted addresses:', error);
    }
  };

  // Calculate required credits based on box weights
  const calculateRequiredCredits = (boxes) => {
    let runningTotal = 0;
    boxes.forEach((box) => {
      const weightLbs = box.weight * 2.20462; // Convert kg to lbs
      let amount = 0;

      if (weightLbs < 10) {
        amount = 3.99;
      } else if (weightLbs >= 10 && weightLbs < 20) {
        amount = 5.99;
      } else if (weightLbs >= 20 && weightLbs < 40) {
        amount = 9.99;
      } else {
        amount = 13.99;
      }

      runningTotal += amount;
    });

    setRequiredCredits(parseFloat(runningTotal.toFixed(2)));
  };

  // Fetch API key for the client
  const fetchApiKey = async (clientName) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/getStallionApiKey`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ clientName }),
      });
      const data = await response.json();

      if (data.apiKey) {
        setApiKey(data.apiKey);
        setClientName(clientName);
        fetchCredits(data.apiKey);
        setError("");
      } else {
        setError("API key not found for client: " + clientName);
      }
    } catch (error) {
      console.error("Error fetching API key:", error);
      setError("Failed to fetch API key. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch available credits
  const fetchCredits = async (apiKey) => {
    if (testMode) return; // Skip credit fetching in test mode
    try {
      const creditsResponse = await fetch(
        "https://ship.stallionexpress.ca/api/v3/credits",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );
      const creditsData = await creditsResponse.json();
      setCredits(parseFloat(creditsData.credits));
    } catch (error) {
      console.error("Error fetching credits:", error);
      setError("Failed to fetch credits. Please try again.");
    }
  };

  const getPrice = (asin) => {
    const data = asinData.find((item) => item.ASIN === asin);
    return data ? parseFloat(data.VALUE) : 0;
  };

  const calculateBoxValue = (box) => {
    return box.items.reduce((acc, item) => {
      return acc + getPrice(item.ASIN) * item.QTY;
    }, 0);
  };

  const handlePurchaseAmountChange = (event) => {
    const amount = event.target.value;
    setPurchaseAmount(amount);
  };

  const handlePurchaseCredits = async () => {
    if (!purchaseAmount || parseFloat(purchaseAmount) <= 0) {
      setError("Please enter a valid purchase amount.");
      return;
    }

    setPurchaseLoading(true);
    try {
      const response = await fetch(
        "https://ship.stallionexpress.ca/api/v3/credits",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            amount: parseFloat(purchaseAmount),
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        setCredits(parseFloat(result.credits));
        setPurchaseAmount("");
        setError("");
      } else {
        setError("Failed to purchase credits. Please try again.");
      }
    } catch (error) {
      console.error("Error purchasing credits:", error);
      setError("Failed to purchase credits. Please try again.");
    } finally {
      setPurchaseLoading(false);
    }
  };

  const fetchImportInfo = async (asins) => {
    try {
      const response = await fetch(`${API_URL}/api/fetch-import-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ asins }),
      });

      if (!response.ok) {
        console.error('Failed to fetch import info:', response.statusText);
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        const importInfoMap = {};
        data.importInfo.forEach(info => {
          importInfoMap[info.asin] = {
            country_of_origin: info.country_of_origin,
            hts_code: info.hts_code
          };
        });
        setImportInfo(importInfoMap);
      } else {
        console.error('Error in import info response:', data.error);
      }
    } catch (error) {
      console.error('Error fetching import info:', error);
    }
  };

  // Function to show test data overview without submitting to Stallion
  const showTestDataOverview = () => {
    console.log('\n=== TRACKING CODE IMPLEMENTATION TEST OVERVIEW ===\n');
    
    console.log('1. Selected Plan Data:');
    console.log('   Plan ID:', selectedPlan?.id || 'Not selected');
    console.log('   Plan Name:', selectedPlan?.name || 'Not selected');
    
    console.log('\n2. Tracking Code Mappings:');
    if (Object.keys(trackingCodeMap).length > 0) {
      Object.entries(trackingCodeMap).forEach(([boxNumber, trackingCode]) => {
        console.log(`   Box ${boxNumber}: ${trackingCode}`);
      });
    } else {
      console.log('   No tracking codes found. Make sure a plan with confirmed placement options is selected.');
    }
    
    console.log('\n3. Boxes and Their Tracking Codes:');
    if (boxes && boxes.length > 0) {
      boxes.forEach((box, index) => {
        const trackingCode = getTrackingCodeForBox(box.boxNumber);
        console.log(`   Box ${box.boxNumber}:`);
        console.log(`     - Dimensions: ${box.length}×${box.width}×${box.height} cm`);
        console.log(`     - Weight: ${box.weight} kg`);
        console.log(`     - Tracking Code: ${trackingCode || 'NOT FOUND'}`);
        console.log(`     - Items: ${box.items?.length || 0} items`);
      });
    } else {
      console.log('   No boxes data available. Using sample data for demonstration...');
      
      // Use sample boxes for demonstration
      const sampleBoxes = [
        { boxNumber: "1", length: 60, width: 41, height: 36, weight: 19, items: [{ ASIN: "B0F1NLR7TX", TITLE: "Sample Item", QTY: 84 }] },
        { boxNumber: "2", length: 60, width: 41, height: 36, weight: 19, items: [{ ASIN: "B0F1NLR7TX", TITLE: "Sample Item", QTY: 84 }] },
        { boxNumber: "3", length: 60, width: 41, height: 36, weight: 19, items: [{ ASIN: "B0F1NLR7TX", TITLE: "Sample Item", QTY: 84 }] },
        { boxNumber: "4", length: 50, width: 40, height: 40, weight: 11, items: [{ ASIN: "B0F1NLR7TX", TITLE: "Sample Item", QTY: 48 }] }
      ];
      
      sampleBoxes.forEach(box => {
        const trackingCode = getTrackingCodeForBox(box.boxNumber);
        console.log(`   Box ${box.boxNumber} (SAMPLE):`);
        console.log(`     - Dimensions: ${box.length}×${box.width}×${box.height} cm`);
        console.log(`     - Weight: ${box.weight} kg`);
        console.log(`     - Tracking Code: ${trackingCode || 'NOT FOUND'}`);
        console.log(`     - Items: ${box.items?.length || 0} items`);
      });
    }
    
    console.log('\n4. Sample Stallion API Payload for Box 1:');
    
    // Use either real box data or sample data
    let testBox;
    let testTrackingCode;
    
    if (boxes && boxes.length > 0) {
      testBox = boxes[0];
      testTrackingCode = getTrackingCodeForBox(testBox.boxNumber);
      console.log('   Using LIVE box data:');
    } else {
      // Use sample data when live data is not available
      testBox = {
        boxNumber: "1",
        length: 60,
        width: 41,
        height: 36,
        weight: 19,
        items: [
          {
            ASIN: "B0F1NLR7TX",
            TITLE: "Canadian Vita Coffee Sample Item",
            QTY: 84
          }
        ]
      };
      testTrackingCode = "1Z10074A2037441087"; // Sample tracking code
      console.log('   Using SAMPLE box data (live data not available):');
    }
    
    // Calculate box value using either real or sample method
    let boxValue;
    try {
      if (boxes && boxes.length > 0) {
        boxValue = calculateBoxValue(testBox);
      } else {
        // Calculate sample value
        boxValue = testBox.items.reduce((acc, item) => acc + (15.99 * item.QTY), 0);
      }
    } catch (error) {
      console.warn('Error calculating box value, using sample value:', error);
      boxValue = 1343.16; // Sample value
    }
    
    // Get client name or use sample
    const testClientName = clientName || "Sample Client Name";
    
    const samplePayload = {
      name: testClientName === "2637061 Ontario Inc (Ye Jin/Alex) (US)" ? "AMZ JIN" : testClientName.substring(0, 40),
      address1: "110 Aeropark Blvd", // Sample address
      city: "MOUNT HOPE",
      province_code: "ON",
      postal_code: "L0R 1W1",
      country_code: "CA",
      weight_unit: "kg",
      weight: parseFloat(testBox.weight),
      length: parseFloat(testBox.length),
      width: parseFloat(testBox.width),
      height: parseFloat(testBox.height),
      size_unit: "cm",
      items: testBox.items?.map((item) => ({
        currency: "USD",
        description: item.TITLE?.slice(0, 40) || "Sample Item",
        quantity: item.QTY,
        sku: item.ASIN,
        value: boxes && boxes.length > 0 ? getPrice(item.ASIN) : 15.99,
        country_of_origin: importInfo[item.ASIN]?.country_of_origin || 'CN',
        ...(importInfo[item.ASIN]?.country_of_origin === 'CN' && importInfo[item.ASIN]?.hts_code && { 
          hs_code: importInfo[item.ASIN].hts_code 
        })
      })) || [],
      value: boxValue,
      currency: "USD",
      package_type: "parcel",
      postage_type: "ups_standard",
      signature_confirmation: false,
      label_format: "png",
      purchase_label: true,
      is_fba: true,
      tracking_code: testTrackingCode, // This is the new field we're adding
    };
    
    console.log('   Box Details:');
    console.log(`     - Box Number: ${testBox.boxNumber}`);
    console.log(`     - Dimensions: ${testBox.length}×${testBox.width}×${testBox.height} cm`);
    console.log(`     - Weight: ${testBox.weight} kg`);
    console.log(`     - Value: $${boxValue.toFixed(2)}`);
    console.log(`     - Tracking Code: ${testTrackingCode || 'NOT FOUND'}`);
    
    console.log('\n   Full Stallion API Payload:');
    console.log(JSON.stringify(samplePayload, null, 2));
    
    console.log('\n5. YYZ Prep API Data Structure Analysis:');
    if (selectedPlan?.placement_options) {
      const selectedOption = selectedPlan.placement_options.find(option => option.is_selected);
      if (selectedOption?.shipments?.[0]) {
        const shipment = selectedOption.shipments[0];
        console.log('   Selected Placement Option ID:', selectedOption.id);
        console.log('   Shipment Confirmation ID:', shipment.shipment_confirmation_id);
        
        if (shipment.tracking_details?.spd_tracking_detail?.spd_tracking_items) {
          console.log('   Available Tracking Items:');
          shipment.tracking_details.spd_tracking_detail.spd_tracking_items.forEach(item => {
            console.log(`     - Box ID: ${item.box_id}, Tracking: ${item.tracking_id}`);
          });
        }
        
        if (shipment.boxes) {
          console.log('   Box Number Mappings:');
          shipment.boxes.forEach(box => {
            console.log(`     - Amazon Box ID: ${box.amazon_box_id} → Internal Box Number: ${box.box_number}`);
          });
        }
      } else {
        console.log('   No selected placement option found or no shipment data.');
      }
    } else {
      console.log('   No placement options available. Using sample data structure:');
      console.log('   Sample mapping: FBA18YBVQ5CMU000002 → Box 1 → 1Z10074A2037441087');
      console.log('   Sample mapping: FBA18YBVQ5CMU000001 → Box 2 → 1Z10074A2037376872');
      console.log('   Sample mapping: FBA18YBVQ5CMU000003 → Box 3 → 1Z10074A2025629497');
      console.log('   Sample mapping: FBA18YBVQ5CMU000004 → Box 4 → 1Z10074A2006226109');
    }
    
    console.log('\n6. Implementation Status:');
    console.log('✅ Tracking code extraction function implemented');
    console.log('✅ Stallion API payload includes tracking_code field');
    console.log('✅ Box number to tracking code mapping functional');
    console.log('✅ Test data available for development/debugging');
    
    const hasRealData = selectedPlan && boxes && boxes.length > 0;
    console.log(`📊 Data Status: ${hasRealData ? 'Using LIVE data' : 'Using SAMPLE data'}`);
    
    if (!hasRealData) {
      console.log('💡 To see live data: Select a plan with confirmed placement options');
    }
    
    console.log('\n=== END TEST OVERVIEW ===\n');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Purchase Labels</h2>
          <p className="text-sm text-slate-600 mt-1">Purchase shipping labels for your boxes</p>
        </div>
        <Badge variant={testMode ? "secondary" : "default"} className="px-3 py-1">
          {testMode ? "Test Mode" : "Live Mode"}
        </Badge>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Test Mode Button */}
      <Card>
        <CardContent className="p-4">
          <Button
            variant="outline"
            onClick={showTestDataOverview}
            className="w-full flex items-center justify-center space-x-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-50"
          >
            <TestTube2 className="w-4 h-4" />
            <span>🧪 Show Tracking Code Test Data (Check Console)</span>
          </Button>
        </CardContent>
      </Card>

      {/* Credits and Purchase Section */}
      {!testMode && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Current Credits */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Current Credits</h3>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-slate-600">Loading credits...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-3xl font-bold text-slate-900">
                    ${credits !== null ? credits.toFixed(2) : "0.00"}
                  </div>
                  <div className="text-sm text-slate-600">
                    Required: <span className="font-semibold">${requiredCredits.toFixed(2)}</span>
                  </div>
                  {credits !== null && credits < requiredCredits && (
                    <div className="text-sm text-red-600 font-medium">
                      ⚠️ Insufficient credits
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Purchase Credits */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold">Purchase Credits</h3>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Amount"
                  value={purchaseAmount}
                  onChange={handlePurchaseAmountChange}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Button
                  onClick={handlePurchaseCredits}
                  disabled={purchaseLoading || !purchaseAmount}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {purchaseLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Purchase"
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Enter the amount to add to your credits balance
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6 text-center">
            <Package className="w-8 h-8 text-blue-600 mx-auto mb-3" />
            <h3 className="font-semibold mb-2">Purchase Labels</h3>
            <p className="text-sm text-slate-600 mb-4">Buy shipping labels for all boxes</p>
            <Button className="w-full bg-blue-600 hover:bg-blue-700">
              <Package className="w-4 h-4 mr-2" />
              Purchase Labels
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <Download className="w-8 h-8 text-green-600 mx-auto mb-3" />
            <h3 className="font-semibold mb-2">UPS Labels</h3>
            <p className="text-sm text-slate-600 mb-4">Download UPS shipping labels</p>
            <Button variant="outline" className="w-full">
              {upsLabelsLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download UPS
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <FileText className="w-8 h-8 text-purple-600 mx-auto mb-3" />
            <h3 className="font-semibold mb-2">Merge Labels</h3>
            <p className="text-sm text-slate-600 mb-4">Combine UPS and Stallion labels</p>
            <Button variant="outline" className="w-full">
              <FileText className="w-4 h-4 mr-2" />
              Merge PDFs
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* File Upload Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Upload className="w-5 h-5 text-slate-600" />
            <h3 className="text-lg font-semibold">Upload Label Files</h3>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* UPS File Upload */}
            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragOverUps ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
            }`}>
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-900 mb-2">UPS Labels PDF</p>
              <p className="text-xs text-slate-500 mb-3">Drag and drop or click to select</p>
              {upsFileName && (
                <div className="text-xs text-green-600 font-medium mb-2">
                  ✓ {upsFileName}
                </div>
              )}
              <Button size="sm" variant="outline">
                Select UPS File
              </Button>
            </div>

            {/* Stallion File Upload */}
            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragOverStallion ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
            }`}>
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-900 mb-2">Stallion Labels PDF</p>
              <p className="text-xs text-slate-500 mb-3">Drag and drop or click to select</p>
              {stallionFileName && (
                <div className="text-xs text-green-600 font-medium mb-2">
                  ✓ {stallionFileName}
                </div>
              )}
              <Button size="sm" variant="outline">
                Select Stallion File
              </Button>
            </div>
          </div>
          
          {/* Merge Button */}
          <div className="flex justify-center pt-4">
            <Button 
              disabled={!upsFile || !stallionFile}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <FileText className="w-4 h-4 mr-2" />
              Merge Uploaded Labels
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Success Message */}
      {purchaseComplete && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Labels purchased successfully! Check your downloads folder for the PDF file.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default PurchaseLabels;